/**
 * Imports - External
 * --------------------------------------------------------------------------
 */

import { faDna, faHospital } from '@fortawesome/duotone-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, BoxProps, CircularProgress, IconButton, List, MenuItem, Paper, Skeleton, Tooltip, Typography, styled, useTheme } from '@mui/material';
import { grey } from '@mui/material/colors';
import { FC, useCallback, useContext, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Imports - Internal
 * --------------------------------------------------------------------------
 */

import { DeleteTrialParticipantCacheData, TrialMetadata } from '@/apis/dtos';
import Loading from '@/components/layout/Loading';
import Show from '@/components/layout/Show';
import TrialsMetadataProvider from '@/components/providers/data/TrialsMetadataProvider';
import TrialsContext from '@/contexts/data/TrialsContext';
import TrialsMetadataContext from '@/contexts/data/TrialsMetadataContext';
import DefaultLayoutContext from '@/contexts/layout/DefaultLayoutContext';
import useServiceStackClient from '@/hooks/apis/useServiceStackClient';
import { useBreadcrumbs } from '@/contexts/layout/BreadcrumbContext';
import { faArrowsRotate } from '@fortawesome/pro-duotone-svg-icons';

/**
 * Interfaces
 * --------------------------------------------------------------------------
 */

interface ITrialListItemProps {
    index: number;
    trial: TrialMetadata;
    lastItem?: boolean;
}

/**
 * Constants
 * --------------------------------------------------------------------------
 */

const themes = {
    allPendingColor: '#777',
    allPendingBackground: '0',
    allPendingBorder: '0',
    allPendingTotal: '#d1d1d1', //#a3a3a3
    allPendingTotalBorder: '2px solid #a8a8a8',

    somePendingColor: '#466591',
    somePendingBackground: '#547eb855',
    somePendingBorder: '2px solid #46659155',
    somePendingTotal: '#b1d1fc',
    somePendingTotalBorder: '2px solid #82a2cf',

    nonePendingColor: '#466560',
    nonePendingBackground: '#67b33e55',
    nonePendingBorder: '2px solid #50803755',
    nonePendingTotal: '#c7f7ad',
    nonePendingTotalBorder: '2px solid #c7f7ad'
}

/**
 * Components
 * --------------------------------------------------------------------------
 */

const VerticalCenter = styled(Box, { shouldForwardProp: (props) => props !== 'toRight' && props !== 'toCenter' })<BoxProps & { toRight?: boolean, toCenter?: boolean }>(({ toRight, toCenter }) => ({
    display: 'flex',
    justifyContent: toCenter ? 'center' : toRight ? 'right' : 'left',
    alignItems: 'center',
}));

const StyledIconButton = styled(IconButton)(() => (({
    width: '36px',
    height: '36px'
})));

const TrialListItem: FC<ITrialListItemProps> = ({
    index,
    trial,
    lastItem
}) => {
    const theme = useTheme();

    const navigate = useNavigate();

    const handleRatio = useCallback((verified: number, total: number) => {
        if (total <= 0) {
            return 0;
        } 
        if (verified > total) {
            return 100;
        }
        return verified / total * 100;
    }, []);

    const ratio = useMemo(() => {
        var verified = trial.verifiedDocuments ?? 0;
        var total = trial.totalDocuments ?? 0;

        if (verified > total) {
            verified = total;
        }

        return handleRatio(verified, total);
    }, [trial.verifiedDocuments, trial.totalDocuments, handleRatio]);

    const verified = useMemo(() => {
        if ((trial?.verifiedDocuments ?? 0) > (trial?.totalDocuments ?? 0)) {
            return trial?.totalDocuments;
        }
        return trial?.verifiedDocuments;
    }, [trial.verifiedDocuments, trial.totalDocuments]);

    return (
        <MenuItem sx={{
            borderBottom: `${lastItem ? 'none' : `1px solid ${grey[200]}`}`,
            backgroundColor: `${index % 2 === 1 ? '#f5f5f5' : "#fff"}`,
            transition: '0.15s',
            padding: 0
        }}
            onClick={() => navigate(`/${trial.oid}`)}
        >
            <Box 
                sx={{ 
                    position: 'absolute',
                    top: 0,
                    left: '60px',
                    right: 0,
                    height: '100%'
                }}
            >
                <Box 
                    sx={{
                        top: 0,
                        width: `${ratio}%`,
                        height: '100%',
                        backgroundColor: `${ratio <= 0 ?
                                themes.allPendingBackground :
                            ratio > 0 && ratio < 100 ?
                                themes.somePendingBackground :
                                themes.nonePendingBackground}`,
                        border: `${ratio <= 0 ? 
                                themes.allPendingBorder : 
                            ratio > 0 && ratio < 100  ?
                                themes.somePendingBorder :
                                themes.nonePendingBorder}`,
                        transition: '0.2s'
                    }}
                >

                </Box>
            </Box>
            <VerticalCenter>
                <VerticalCenter sx={{
                    backgroundColor: theme.palette.primary.main,
                    width: '60px',
                    height: '60px'
                }} toCenter>
                    <FontAwesomeIcon color='white' fontSize='2.0rem' icon={faHospital} />
                </VerticalCenter>
                <VerticalCenter sx={{
                    minWidth: '190px',
                    height: '60px',
                    padding: '0 8px'
                }}>
                    <List>
                        <Typography variant='body1'>{trial.name}</Typography>
                        <Box sx={{ display: 'inline', fontSize: '0.8rem' }}>{trial.uniqueIdentifier}</Box>
                    </List>
                </VerticalCenter>
            </VerticalCenter>
            <VerticalCenter toRight sx={{ width: '100%', height: '100%', paddingRight: '8px' }}>
                {
                    trial.totalDocuments !== undefined && trial.verifiedDocuments !== undefined ?
                    <Box sx={{
                        padding: '5px 10px',
                        backgroundColor: `${ratio <= 0 ?
                                themes.allPendingTotal :
                            ratio > 0 && ratio < 100 ?
                                themes.somePendingTotal :
                                themes.nonePendingBackground}`,
                        border: `${ratio <= 0 ? 
                                themes.allPendingTotalBorder : 
                            ratio > 0 && ratio < 100 ?
                                themes.somePendingTotalBorder :
                                themes.nonePendingBorder}`,
                        transition: '0.2s',
                        borderRadius: '8px'
                    }}>
                        <Typography 
                            variant='body1'
                            color={
                                ratio <= 0 ?
                                    themes.allPendingColor :
                                ratio > 0 && ratio < 100 ?
                                    themes.somePendingColor :
                                    themes.nonePendingColor
                            }
                            fontWeight='500'
                        >
                            {verified} / {trial.totalDocuments}
                        </Typography>
                    </Box> :
                    <Skeleton width='80px' height='60px'/>
                }
            </VerticalCenter>
        </MenuItem>
    )
}

const TrialsContent: FC = () => {
    const theme = useTheme();
    const client = useServiceStackClient();

    const { data: trials, loading: trialsLoading, validating: trialsValidating } = useContext(TrialsContext);
    const { data: trialsMeta, loading: trialsMetaLoading, validating: trialsMetaValidating, refresh } = useContext(TrialsMetadataContext);

    const onCallDeleteCache = useCallback(async () => {
        const request = new DeleteTrialParticipantCacheData ({});

        await client.post(request);
    }, [client]);

    const onRefreshClick = useCallback(async () => {
        await onCallDeleteCache();

        await refresh();
    }, [onCallDeleteCache, refresh]);

    const filteredTrialData = useMemo(() => {
        // If we have the trials meta data
        if (trialsMeta) {
            const data = trialsMeta?.filter(x => x.name !== 'Default Study' && x.name) ?? [];
            return data.sort((a, b) => {
                if (a.name! < b.name!) {
                    return -1;
                }
                if (a.name! > b.name!) {
                    return 1;
                }
                return 0;
            });
        }
        // If trials metadata has not loaded but trials data has
        else if (trials) {
            const data = trials?.filter(x => x.name !== 'Default Study' && x.name) ?? [];

            return data.sort((a, b) => {
                if (a.name! < b.name!) {
                    return -1;
                }
                if (a.name! > b.name!) {
                    return 1;
                }
                return 0;
            });
        }
        
        return [];
    }, [trials, trialsMeta])

    const loading = trialsLoading || trialsMetaLoading;
    const validating = trialsValidating || trialsMetaValidating;

    return (
        <>
            <Loading name='trials' isLoading={loading} isValidating={validating} />
            <Show show={!loading}>
                <VerticalCenter marginBottom='10px'>
                    <Typography display="inline" color='secondary' variant='h2'>Trials</Typography>
                    <VerticalCenter sx={{ width: '100%' }} display="inline" toRight gap={theme.spacing(1)}>
                        <Tooltip
                            title="Refresh Total Document Values"
                        >
                            <Box>
                                <StyledIconButton
                                    color="primary"
                                    disabled={trialsMetaLoading || trialsMetaValidating}
                                    onClick={onRefreshClick}
                                >
                                    {
                                        trialsMetaLoading || trialsMetaValidating ? 
                                            <CircularProgress size={18} /> :
                                            <FontAwesomeIcon icon={faArrowsRotate} size="xs"/>
                                    }
                                </StyledIconButton>
                            </Box>
                        </Tooltip>
                        <Box sx={{
                            backgroundColor: `#F9CBDF`,
                            padding: '4px 8px',
                            borderRadius: '15px'
                        }}>
                            <Typography fontSize='0.8rem' fontWeight='500' color={theme.palette.secondary.main} variant="body2">Total documents verified</Typography>
                        </Box>
                    </VerticalCenter>
                </VerticalCenter>
                <Paper>
                    <List sx={{ padding: '0' }}>
                        {
                            filteredTrialData && filteredTrialData.map((t, idx) => (
                                <TrialListItem 
                                    key={`trial-${t.id}`}
                                    index={idx}
                                    trial={t}
                                    lastItem={idx + 1 === filteredTrialData.length}
                                />
                            ))
                        }
                    </List>
                </Paper>
            </Show>
        </>
    )
}

const Trials: FC = () => {
    const { setBreadcrumbs } = useBreadcrumbs();
    const { setTrialLinkEnabled } = useContext(DefaultLayoutContext);

    useEffect(() => {
        if(setTrialLinkEnabled) {
            setTrialLinkEnabled(false);
        }
    }, [setTrialLinkEnabled]);

    useEffect(() => {
        setBreadcrumbs([ { label: "Trials", to: "/", icon: faDna } ]);
    }, [setBreadcrumbs]);

    return (
        <TrialsMetadataProvider>
            <TrialsContent />
        </TrialsMetadataProvider>
    )
}

export default Trials